import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Header } from "@/app/modules/Navigation";
import { ScrollToTopButton, SearchBar } from "@/components";
import { useAppProperties } from "@/hooks";
import { useAmityAuth, useTranslate } from "@/providers/context";
import { cn, getUrlFromFilename } from "@/utils";

import {
  BackButton,
  CommunityPostDetails,
  EmptyScreen,
  JoinLeaveButton,
  PostsList,
} from "../";
import { SEARCH_PARAM } from "../../const";
import { isAutoJoinGroup } from "../../util";
import { useCommunityFeed } from "../../providers/context/useCommunityFeed";

const SCROLL_CONTAINER_ID = "ScrollContainer--CommunityFeed";

export const CommunityFeed = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { communityGroupsEnabled, communitySearchEnabled } = useAppProperties();
  const [searchParams, setSearchParams] = useSearchParams();
  const { translate } = useTranslate();
  const {
    userBanned,
    loadingInitialContent,
    group,
    restoreScrollToPost,
    posts,
  } = useCommunityFeed();

  const queryParameters = useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams],
  );

  const query = searchParams.get(SEARCH_PARAM);

  const { authState, isGlobalBanned, isError, communityTabName } =
    useAmityAuth();

  const handleSearch = useCallback(
    (value: string) => {
      if (value.trim() !== "") {
        queryParameters.set(SEARCH_PARAM, value);
      } else {
        queryParameters.delete(SEARCH_PARAM);
      }
      setSearchParams(queryParameters);
    },
    [queryParameters, setSearchParams],
  );

  const hasBanner = useMemo(
    () => !!communityGroupsEnabled && !!group.BannerURL,
    [communityGroupsEnabled, group.BannerURL],
  );

  useEffect(() => {
    if (!postId && !loadingInitialContent && posts.length > 0) {
      restoreScrollToPost();
    }
  }, [postId, loadingInitialContent, posts.length, restoreScrollToPost]);

  const getHeaderContent = useCallback(() => {
    if (query) {
      return (
        <BackButton
          text={communityGroupsEnabled ? group.Name : translate("Feed")}
          onClick={() => handleSearch("")}
        />
      );
    }

    if (communityGroupsEnabled) {
      return (
        <BackButton
          text={communityTabName}
          onClick={() => navigate("/community")}
        />
      );
    }

    return <div className="h5-bold">{communityTabName}</div>;
  }, [
    communityGroupsEnabled,
    query,
    communityTabName,
    group.Name,
    navigate,
    translate,
    handleSearch,
  ]);

  if (postId && !isGlobalBanned && !userBanned) {
    return <CommunityPostDetails postId={postId} parentGroup={group} />;
  }

  return (
    <div className="h-full">
      <Header
        className="px-7"
        content={getHeaderContent()}
        rightContent={
          communitySearchEnabled && (
            <SearchBar
              className="pr-0"
              value={query ?? ""}
              onSearch={handleSearch}
              collapsible
              showClearButton
            />
          )
        }
      />
      {isGlobalBanned || userBanned ? (
        <EmptyScreen screenType="banned" />
      ) : !loadingInitialContent &&
        (isError || !authState?.authenticatedUser) ? (
        <EmptyScreen screenType="error" />
      ) : (
        <div
          className="relative flex h-[calc(100%-94px)] w-full justify-center overflow-y-auto"
          id={SCROLL_CONTAINER_ID}
        >
          <div className="flex min-h-full w-[624px] flex-col lg:w-[795px] xl:w-[975px]">
            {hasBanner && !query && (
              <div className="mb-8 ml-3 aspect-[2.53/1] w-full shrink-0 overflow-hidden rounded-xl bg-surface-thumbnail lg:mb-10 lg:ml-0">
                <img
                  src={
                    group.BannerURL
                      ? getUrlFromFilename(group.BannerURL)
                      : undefined
                  }
                  alt={group.Name}
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <div
              className={cn(
                "flex grow flex-col lg:flex-row",
                communityGroupsEnabled && !query
                  ? "justify-start lg:justify-between"
                  : "justify-center",
              )}
            >
              {communityGroupsEnabled && !query && (
                <div className="mb-8 ml-3 flex h-fit w-full flex-col gap-3 border-b border-border pb-8 lg:sticky lg:top-0 lg:mb-0 lg:ml-0 lg:mr-8 lg:w-[307px] lg:border-none">
                  <div className="flex items-center justify-between">
                    <div className="lg:h5-bold h4-extrabold break-words">
                      {group.Name}
                    </div>
                    {!isAutoJoinGroup(group) && (
                      <JoinLeaveButton className="lg:hidden" />
                    )}
                  </div>
                  <div className="p2-regular whitespace-pre-wrap break-words">
                    {group.Description}
                  </div>
                  {!isAutoJoinGroup(group) && (
                    <JoinLeaveButton className="mt-6 hidden lg:flex" />
                  )}
                </div>
              )}
              <PostsList scrollableTarget={SCROLL_CONTAINER_ID} />
            </div>
          </div>
          <ScrollToTopButton scrollContainerId={SCROLL_CONTAINER_ID} />
        </div>
      )}
    </div>
  );
};
